import { FC } from 'react';
import moment from 'moment';
import {
  type CarbonIconType,
  Chat,
  Document,
  DocumentAdd,
  Flag,
  FlowConnection,
  Migrate,
  Pin,
} from '@carbon/react/icons';
import { Link } from 'components/link';
import type { NotificationKind, NotificationRow as Row } from './types';
import styles from './notification-feed.module.scss';

// CarbonIconType rather than FC<{size}> — Carbon's own icon type allows a string size, so
// a narrower hand-written signature is not assignable to it.
const KIND_ICONS: Record<NotificationKind, CarbonIconType> = {
  status_changed: FlowConnection,
  severity_changed: Flag,
  commented: Chat,
  mentioned: Chat,
  moved: Migrate,
  area_updated: Document,
  area_new_wells: Pin,
  area_new_documents: DocumentAdd,
};

/**
 * The one-line summary. Written from the payload alone — the subject is deliberately not
 * loaded — so this reads correctly even for a report that has since been deleted.
 *
 * @param {Row} row - The notification to describe.
 * @return {string} One line of prose for the row.
 */
const summarize = (row: Row): string => {
  const { payload } = row;

  switch (row.kind) {
    case 'status_changed':
      return `Status changed from ${payload.from ?? '—'} to ${
        payload.to ?? '—'
      }`;

    case 'severity_changed': {
      const direction = payload.raised ? 'raised' : 'lowered';
      const to = payload.toLabel ?? payload.to ?? '—';
      // No previous label means it was set for the first time rather than moved, and
      // "lowered from nothing" would be nonsense.
      return payload.fromLabel
        ? `Severity ${direction} from ${payload.fromLabel} to ${to}`
        : `Severity set to ${to}`;
    }

    case 'commented': {
      const count = payload.count ?? 1;
      const who = payload.authorName;
      if (count > 1) {
        return who
          ? `${count} new comments, latest from ${who}`
          : `${count} new comments`;
      }
      return who ? `${who} commented` : 'New comment';
    }

    case 'mentioned': {
      // Names the person, because "you were mentioned" without saying by whom is a worse
      // prompt than the sentence it is summarising. The excerpt follows on the row's second
      // line, so this stays the headline rather than repeating it.
      const who = payload.authorName;

      return who ? `${who} mentioned you` : 'You were mentioned';
    }

    case 'moved': {
      // Names the new key, because the move rewrote it: anyone holding the old reference
      // needs the replacement, not just the fact that something happened.
      const to = String(payload.to ?? '');
      const board = String(payload.boardName ?? '');

      if (!to) return 'Moved to another Jira board';

      return board ? `Moved to ${board} as ${to}` : `Moved to ${to}`;
    }

    case 'area_updated':
    case 'area_new_wells':
    case 'area_new_documents':
      return row.message?.title ?? payload.title ?? 'Area updated';

    default:
      return 'Updated';
  }
};

interface NotificationRowProps {
  row: Row;
  onSelect: (row: Row) => void;
}

export const NotificationRow: FC<NotificationRowProps> = ({
  row,
  onSelect,
}) => {
  const Icon = KIND_ICONS[row.kind] ?? Chat;
  const ticket = row.payload.jiraIssueKey;

  const body = (
    <>
      <span className={styles.rowIcon}>
        <Icon size={16} />
      </span>
      <span className={styles.rowBody}>
        <span className={styles.rowSummary}>
          {summarize(row)}
          {/*
            Only shown when it stands for more than one event. Without it an aggregated row
            silently under-reports — "moved from To Do to Done" hides that it went through
            In Progress on the way.
          */}
          {row.groupCount > 1 ? (
            <span
              className={styles.groupCount}
            >{`${row.groupCount} updates`}</span>
          ) : null}
        </span>
        {/*
          Which report this is about. On the full page this is the leftmost column and the
          one the search box matches; in the panel it sits under the summary. The title is
          its own element so it can be the part that truncates — a clipped Jira key is
          useless, a clipped title still reads.
        */}
        <span className={styles.rowSubject}>
          {ticket ? <span className={styles.rowTicket}>{ticket}</span> : null}
          <span className={styles.rowTitle}>
            {row.message ? row.message.body : row.payload.title}
          </span>
        </span>
        {/*
          What they actually said, on mention rows — the only kind that carries one.

          Not previously unrendered, which is how the review thread described it and how the
          first version of this comment repeated it: BugsFeatureMailer#sentence_for has
          always appended it to the emailed line. What was missing was the in-app half, so
          the same notification read "Jason Kock mentioned you: @Lando can you look…" in the
          inbox and "Jason Kock mentioned you" in the panel — and the note on #summarize
          promised a second line the panel never drew. The two surfaces agree now.
        */}
        {row.payload.excerpt ? (
          <span className={styles.rowExcerpt}>{row.payload.excerpt}</span>
        ) : null}
        {/* Absolute time on hover — "2 hours ago" is the useful default, but anyone
            reconstructing a sequence of events needs the timestamp. */}
        <span
          className={styles.rowTime}
          title={moment(row.createdAt).format('MMM D, YYYY h:mm A')}
        >
          {moment(row.createdAt).fromNow()}
        </span>
      </span>
      {/* The dot is the only unread affordance that survives the row being visited, since
          the tint disappears the moment it is marked read. */}
      {!row.read ? (
        <span className={styles.unreadDot} aria-label="Unread" />
      ) : null}
    </>
  );

  const className = `${styles.row} ${row.read ? '' : styles.rowUnread}`;

  // A notification whose subject is gone still renders — it just is not a link any more.
  if (!row.href) {
    return (
      <div className={className} role="listitem">
        {body}
      </div>
    );
  }

  return (
    <Link to={row.href} className={className} onClick={() => onSelect(row)}>
      {body}
    </Link>
  );
};
